import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import {
  getProfile, updateProfile, changePassword,
  getPractice, updatePractice, getAuditLog,
  totpSetup, totpEnable, totpDisable, regenerateBackupCodes,
  getStripeConnectStatus, initiateStripeOnboarding, disconnectStripe, getStripeDashboardLink,
  enableWidget, regenerateWidgetToken,
} from '../services/settingsService';
import { getZoomStatus, getZoomAuthUrl, completeZoomOAuth, disconnectZoom } from '../services/integrationsService';
import { getSubscriptionStatus, createCheckoutSession, createPortalSession } from '../services/subscriptionService';
import {
  getCalendarSubscriptions,
  createCalendarSubscription,
  updateCalendarSubscription,
  deleteCalendarSubscription,
  refreshCalendarSubscription,
} from '../services/calendarSubscriptionService';
import { getReminderSettings, updateReminderSettings } from '../services/reportsService';
import {
  listAppointmentTypes,
  createAppointmentType,
  updateAppointmentType,
  deleteAppointmentType,
} from '../services/appointmentTypeService';
import {
  getFeeScheduleItems,
  createFeeScheduleItem,
  updateFeeScheduleItem,
  deactivateFeeScheduleItem,
  getPayerRates,
  createPayerRate,
  updatePayerRate,
  deletePayerRate,
} from '../services/feeScheduleService';
import Spinner from '../components/common/Spinner';

const TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
];

const profileSchema = z.object({
  firstName: z.string().min(1, 'Required').max(100),
  lastName: z.string().min(1, 'Required').max(100),
});

const clinicalSchema = z.object({
  npi: z.string().max(10).optional().or(z.literal('')),
  taxonomyCode: z.string().max(20).optional().or(z.literal('')),
  credentials: z.string().max(50).optional().or(z.literal('')),
  licenseNumber: z.string().max(50).optional().or(z.literal('')),
  licenseState: z.string().max(2).optional().or(z.literal('')),
});

const passwordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Required'),
    newPassword: z
      .string()
      .min(8, 'Must be at least 8 characters')
      .regex(/[A-Z]/, 'Must contain an uppercase letter')
      .regex(/[0-9]/, 'Must contain a number'),
    confirmPassword: z.string(),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

const practiceSchema = z.object({
  name: z.string().min(1, 'Required').max(255),
  email: z.union([z.string().email(), z.literal('')]).optional(),
  phone: z.string().max(20).optional().or(z.literal('')),
  website: z.string().optional().or(z.literal('')),
  timezone: z.string().optional(),
  addressLine1: z.string().max(255).optional().or(z.literal('')),
  addressLine2: z.string().max(255).optional().or(z.literal('')),
  city: z.string().max(100).optional().or(z.literal('')),
  state: z.string().max(2).optional().or(z.literal('')),
  zip: z.string().max(10).optional().or(z.literal('')),
  npi: z.string().max(10).optional().or(z.literal('')),
  ein: z.string().max(20).optional().or(z.literal('')),
  taxonomyCode: z.string().max(20).optional().or(z.literal('')),
});

const Field = ({ label, error, children }) => (
  <div>
    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">{label}</label>
    {children}
    {error && <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">{error.message}</p>}
  </div>
);

function ProfileTab() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['settings', 'profile'],
    queryFn: getProfile,
  });

  const profileForm = useForm({ resolver: zodResolver(profileSchema) });
  const clinicalForm = useForm({ resolver: zodResolver(clinicalSchema) });
  const passwordForm = useForm({ resolver: zodResolver(passwordSchema) });

  useEffect(() => {
    if (data?.user) {
      profileForm.reset({
        firstName: data.user.firstName ?? '',
        lastName: data.user.lastName ?? '',
      });
    }
    if (data?.practitioner) {
      const p = data.practitioner;
      clinicalForm.reset({
        npi: p.npi ?? '',
        taxonomyCode: p.taxonomyCode ?? '',
        credentials: p.credentials ?? '',
        licenseNumber: p.licenseNumber ?? '',
        licenseState: p.licenseState ?? '',
      });
    }
  }, [data, profileForm, clinicalForm]);

  const profileMutation = useMutation({
    mutationFn: updateProfile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'profile'] });
      toast.success('Profile updated');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to update profile'),
  });

  const clinicalMutation = useMutation({
    mutationFn: updateProfile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'profile'] });
      toast.success('Clinical credentials saved');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to save credentials'),
  });

  const passwordMutation = useMutation({
    mutationFn: changePassword,
    onSuccess: () => {
      passwordForm.reset();
      toast.success('Password changed');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to change password'),
  });

  if (isLoading) return <div className="flex justify-center py-10"><Spinner /></div>;

  const user = data?.user;

  return (
    <div className="space-y-8 max-w-lg">
      {/* Profile info */}
      <div className="card">
        <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100 mb-5">Profile</h2>
        <form
          onSubmit={profileForm.handleSubmit((d) => profileMutation.mutate(d))}
          className="space-y-4"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="First name" error={profileForm.formState.errors.firstName}>
              <input type="text" className="input" {...profileForm.register('firstName')} />
            </Field>
            <Field label="Last name" error={profileForm.formState.errors.lastName}>
              <input type="text" className="input" {...profileForm.register('lastName')} />
            </Field>
          </div>
          <Field label="Email">
            <input
              type="email"
              className="input bg-neutral-50 dark:bg-neutral-900 cursor-not-allowed"
              value={user?.email ?? ''}
              disabled
            />
            <p className="mt-1 text-xs text-neutral-400 dark:text-neutral-500">Email cannot be changed.</p>
          </Field>
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={profileMutation.isPending}
              className="btn btn-primary disabled:opacity-60"
            >
              {profileMutation.isPending ? 'Saving…' : 'Save profile'}
            </button>
          </div>
        </form>
      </div>

      {/* Clinical Credentials */}
      <div className="card">
        <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100 mb-1">Clinical Credentials</h2>
        <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-5">
          Required for superbill and CMS-1500 generation. Your individual (Type 1) NPI appears in Box 24J.
        </p>
        <form
          onSubmit={clinicalForm.handleSubmit((d) => clinicalMutation.mutate(d))}
          className="space-y-4"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Individual NPI (Type 1)" error={clinicalForm.formState.errors.npi}>
              <input type="text" className="input font-mono" maxLength={10} placeholder="1234567890" {...clinicalForm.register('npi')} />
            </Field>
            <Field label="Taxonomy Code" error={clinicalForm.formState.errors.taxonomyCode}>
              <input type="text" className="input font-mono" maxLength={20} placeholder="e.g. 101YM0800X" {...clinicalForm.register('taxonomyCode')} />
            </Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Credentials" error={clinicalForm.formState.errors.credentials}>
              <input type="text" className="input" maxLength={50} placeholder="e.g. LMHC, LCSW, PhD" {...clinicalForm.register('credentials')} />
            </Field>
            <Field label="License number" error={clinicalForm.formState.errors.licenseNumber}>
              <input type="text" className="input" maxLength={50} {...clinicalForm.register('licenseNumber')} />
            </Field>
          </div>
          <Field label="License state" error={clinicalForm.formState.errors.licenseState}>
            <input type="text" className="input w-24" maxLength={2} placeholder="NY" {...clinicalForm.register('licenseState')} />
          </Field>
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={clinicalMutation.isPending}
              className="btn btn-primary disabled:opacity-60"
            >
              {clinicalMutation.isPending ? 'Saving…' : 'Save credentials'}
            </button>
          </div>
        </form>
      </div>

      {/* Change password */}
      <div className="card">
        <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100 mb-5">Change Password</h2>
        <form
          onSubmit={passwordForm.handleSubmit((d) => passwordMutation.mutate(d))}
          className="space-y-4"
        >
          <Field label="Current password" error={passwordForm.formState.errors.currentPassword}>
            <input type="password" className="input" {...passwordForm.register('currentPassword')} />
          </Field>
          <Field label="New password" error={passwordForm.formState.errors.newPassword}>
            <input type="password" className="input" {...passwordForm.register('newPassword')} />
          </Field>
          <Field label="Confirm new password" error={passwordForm.formState.errors.confirmPassword}>
            <input type="password" className="input" {...passwordForm.register('confirmPassword')} />
          </Field>
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={passwordMutation.isPending}
              className="btn btn-primary disabled:opacity-60"
            >
              {passwordMutation.isPending ? 'Changing…' : 'Change password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PracticeTab() {
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['settings', 'practice'],
    queryFn: getPractice,
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({ resolver: zodResolver(practiceSchema) });

  useEffect(() => {
    if (data?.practice) {
      const p = data.practice;
      reset({
        name: p.name ?? '',
        email: p.email ?? '',
        phone: p.phone ?? '',
        website: p.website ?? '',
        timezone: p.timezone ?? 'America/Los_Angeles',
        addressLine1: p.addressLine1 ?? '',
        addressLine2: p.addressLine2 ?? '',
        city: p.city ?? '',
        state: p.state ?? '',
        zip: p.zip ?? '',
        npi: p.npi ?? '',
        ein: p.ein ?? '',
        taxonomyCode: p.taxonomyCode ?? '',
      });
    }
  }, [data, reset]);

  const mutation = useMutation({
    mutationFn: updatePractice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'practice'] });
      toast.success('Practice settings saved');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to save practice'),
  });

  if (isLoading) return <div className="flex justify-center py-10"><Spinner /></div>;
  if (isError)
    return <p className="text-sm text-neutral-500 dark:text-neutral-400 py-6">No practice linked to your account.</p>;

  return (
    <div className="max-w-lg">
      <form onSubmit={handleSubmit((d) => mutation.mutate(d))} className="space-y-6">
        <div className="card space-y-4">
          <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">Practice Information</h2>
          <Field label="Practice name *" error={errors.name}>
            <input type="text" className="input" {...register('name')} />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Phone" error={errors.phone}>
              <input type="tel" className="input" {...register('phone')} />
            </Field>
            <Field label="Email" error={errors.email}>
              <input type="email" className="input" {...register('email')} />
            </Field>
          </div>
          <Field label="Website" error={errors.website}>
            <input type="text" className="input" placeholder="https://…" {...register('website')} />
          </Field>
          <Field label="Timezone" error={errors.timezone}>
            <select className="input" {...register('timezone')}>
              {TIMEZONES.map((tz) => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
          </Field>
        </div>

        <div className="card space-y-4">
          <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">Address</h2>
          <Field label="Address line 1" error={errors.addressLine1}>
            <input type="text" className="input" {...register('addressLine1')} />
          </Field>
          <Field label="Address line 2" error={errors.addressLine2}>
            <input type="text" className="input" {...register('addressLine2')} />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="City" error={errors.city}>
              <input type="text" className="input" {...register('city')} />
            </Field>
            <Field label="State" error={errors.state}>
              <input type="text" className="input" maxLength={2} {...register('state')} />
            </Field>
            <Field label="ZIP" error={errors.zip}>
              <input type="text" className="input" {...register('zip')} />
            </Field>
          </div>
        </div>

        <div className="card space-y-4">
          <div>
            <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">Billing &amp; Identification</h2>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
              Required for superbill and CMS-1500 generation. Group NPI (Type 2) appears in Box 33A.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Group NPI (Type 2)" error={errors.npi}>
              <input type="text" className="input font-mono" maxLength={10} placeholder="1234567890" {...register('npi')} />
            </Field>
            <Field label="EIN" error={errors.ein}>
              <input type="text" className="input font-mono" maxLength={20} placeholder="12-3456789" {...register('ein')} />
            </Field>
          </div>
          <Field label="Taxonomy Code" error={errors.taxonomyCode}>
            <input type="text" className="input font-mono w-48" maxLength={20} placeholder="e.g. 101YM0800X" {...register('taxonomyCode')} />
          </Field>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={mutation.isPending}
            className="btn btn-primary disabled:opacity-60"
          >
            {mutation.isPending ? 'Saving…' : 'Save practice'}
          </button>
        </div>
      </form>
    </div>
  );
}

const apptTypeSchema = z.object({
  name: z.string().min(1, 'Required').max(100),
  durationMinutes: z.coerce.number().int().min(1).max(480),
  price: z.union([z.coerce.number().min(0), z.literal('')]).optional(),
  cptCode: z.string().max(10).optional().or(z.literal('')),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Must be a hex color e.g. #4F8EF7').optional().or(z.literal('')),
  description: z.string().max(500).optional().or(z.literal('')),
});

const DURATION_OPTIONS = [15, 30, 45, 50, 53, 60, 75, 90, 120];

const COMMON_CPT_CODES = [
  { code: '90791', label: '90791 — Psychiatric diagnostic evaluation' },
  { code: '90792', label: '90792 — Psychiatric diagnostic evaluation w/ medical services' },
  { code: '90832', label: '90832 — Psychotherapy, 30 min' },
  { code: '90834', label: '90834 — Psychotherapy, 45 min' },
  { code: '90837', label: '90837 — Psychotherapy, 60 min' },
  { code: '90847', label: '90847 — Family psychotherapy with patient, 50 min' },
  { code: '90846', label: '90846 — Family psychotherapy without patient, 50 min' },
  { code: '90853', label: '90853 — Group psychotherapy' },
  { code: '90839', label: '90839 — Psychotherapy for crisis, 60 min' },
];

function AppointmentTypesTab() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(null); // null = closed, 'new' = new form, id = edit form

  const { data: types = [], isLoading } = useQuery({
    queryKey: ['appointmentTypes'],
    queryFn: listAppointmentTypes,
  });

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    control,
    formState: { errors },
  } = useForm({ resolver: zodResolver(apptTypeSchema) });

  const colorValue = useWatch({ control, name: 'color', defaultValue: '#4F8EF7' });

  const openNew = () => {
    reset({ name: '', durationMinutes: 50, price: '', cptCode: '', color: '#4F8EF7', description: '' });
    setEditing('new');
  };

  const openEdit = (t) => {
    reset({
      name: t.name,
      durationMinutes: t.durationMinutes,
      price: t.price ?? '',
      cptCode: t.cptCode ?? '',
      color: t.color ?? '',
      description: t.description ?? '',
    });
    setEditing(t.id);
  };

  const closeForm = () => setEditing(null);

  const createMutation = useMutation({
    mutationFn: createAppointmentType,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointmentTypes'] });
      toast.success('Appointment type created');
      closeForm();
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to create'),
  });

  const updateMutation = useMutation({
    mutationFn: updateAppointmentType,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointmentTypes'] });
      toast.success('Appointment type updated');
      closeForm();
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to update'),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteAppointmentType,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointmentTypes'] });
      toast.success('Appointment type deactivated');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to deactivate'),
  });

  const onSubmit = (data) => {
    const payload = {
      ...data,
      price: data.price === '' ? null : data.price,
      cptCode: data.cptCode === '' ? null : data.cptCode,
      color: data.color === '' ? null : data.color,
      description: data.description === '' ? null : data.description,
    };
    if (editing === 'new') {
      createMutation.mutate(payload);
    } else {
      updateMutation.mutate({ id: editing, ...payload });
    }
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="max-w-2xl space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-neutral-500 dark:text-neutral-400">
          Define the session types your practice offers — used when booking appointments.
        </p>
        {editing === null && (
          <button onClick={openNew} className="btn btn-primary text-sm">
            + New type
          </button>
        )}
      </div>

      {/* Inline form */}
      {editing !== null && (
        <div className="card space-y-4">
          <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">
            {editing === 'new' ? 'New appointment type' : 'Edit appointment type'}
          </h2>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <Field label="Name *" error={errors.name}>
              <input type="text" className="input" {...register('name')} />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Duration (minutes) *" error={errors.durationMinutes}>
                <select className="input" {...register('durationMinutes')}>
                  {DURATION_OPTIONS.map((m) => (
                    <option key={m} value={m}>{m} min</option>
                  ))}
                </select>
              </Field>
              <Field label="Price ($)" error={errors.price}>
                <input type="number" min="0" step="0.01" className="input" {...register('price')} />
              </Field>
            </div>
            <Field label="CPT Code" error={errors.cptCode}>
              <select className="input" {...register('cptCode')}>
                <option value="">— None —</option>
                {COMMON_CPT_CODES.map(({ code, label }) => (
                  <option key={code} value={code}>{label}</option>
                ))}
              </select>
            </Field>
            <Field label="Color" error={errors.color}>
              <div className="flex gap-2 items-center">
                <input
                  type="color"
                  className="h-9 w-12 rounded border border-neutral-300 dark:border-neutral-600 p-0.5 cursor-pointer"
                  value={/^#[0-9A-Fa-f]{6}$/.test(colorValue) ? colorValue : '#4F8EF7'}
                  onChange={(e) => setValue('color', e.target.value, { shouldValidate: true })}
                />
                <input type="text" className="input flex-1" placeholder="#4F8EF7" {...register('color')} />
              </div>
            </Field>
            <Field label="Description" error={errors.description}>
              <textarea className="input" rows={2} {...register('description')} />
            </Field>
            <div className="flex gap-3 justify-end pt-1">
              <button type="button" onClick={closeForm} className="btn btn-secondary text-sm">
                Cancel
              </button>
              <button type="submit" disabled={isSubmitting} className="btn btn-primary text-sm disabled:opacity-60">
                {isSubmitting ? 'Saving…' : editing === 'new' ? 'Create' : 'Save changes'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* List */}
      <div className="card p-0 overflow-x-auto">
        {isLoading ? (
          <div className="flex justify-center py-10"><Spinner /></div>
        ) : types.length === 0 ? (
          <p className="text-sm text-neutral-500 dark:text-neutral-400 text-center py-10">
            No appointment types yet. Add one above.
          </p>
        ) : (
          <table className="w-full text-sm min-w-[600px]">
            <thead className="bg-neutral-50 dark:bg-neutral-900/50 border-b border-neutral-200 dark:border-neutral-700">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-neutral-600 dark:text-neutral-400">Name</th>
                <th className="text-left px-4 py-3 font-medium text-neutral-600 dark:text-neutral-400">Duration</th>
                <th className="text-left px-4 py-3 font-medium text-neutral-600 dark:text-neutral-400">Price</th>
                <th className="text-left px-4 py-3 font-medium text-neutral-600 dark:text-neutral-400">CPT Code</th>
                <th className="text-left px-4 py-3 font-medium text-neutral-600 dark:text-neutral-400">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100 dark:divide-neutral-700">
              {types.map((t) => (
                <tr key={t.id} className={t.isActive ? '' : 'opacity-50'}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {t.color && (
                        <span
                          className="inline-block w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: t.color }}
                        />
                      )}
                      <span className="font-medium text-neutral-900 dark:text-neutral-100">{t.name}</span>
                    </div>
                    {t.description && (
                      <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-0.5 truncate max-w-xs">{t.description}</p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-neutral-600 dark:text-neutral-400">{t.durationMinutes} min</td>
                  <td className="px-4 py-3 text-neutral-600 dark:text-neutral-400">
                    {t.price != null ? `$${parseFloat(t.price).toFixed(2)}` : '—'}
                  </td>
                  <td className="px-4 py-3">
                    {t.cptCode ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400">
                        {t.cptCode}
                      </span>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      t.isActive ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400'
                    }`}>
                      {t.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => openEdit(t)}
                        className="text-xs text-primary-600 hover:text-primary-800 font-medium"
                      >
                        Edit
                      </button>
                      {t.isActive && (
                        <button
                          onClick={() => deleteMutation.mutate(t.id)}
                          disabled={deleteMutation.isPending}
                          className="text-xs text-danger-500 hover:text-danger-700 font-medium disabled:opacity-50"
                        >
                          Deactivate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const ACTION_COLORS = {
  CREATE: 'text-green-700 bg-green-50',
  UPDATE: 'text-blue-700 bg-blue-50',
  DELETE: 'text-danger-700 bg-danger-50',
  READ: 'text-neutral-600 bg-neutral-100',
  VIEW: 'text-neutral-600 bg-neutral-100',
  LOGIN: 'text-purple-700 bg-purple-50',
  LOGOUT: 'text-purple-700 bg-purple-50',
  SIGN: 'text-indigo-700 bg-indigo-50',
  PAYMENT: 'text-emerald-700 bg-emerald-50',
  DOWNLOAD: 'text-sky-700 bg-sky-50',
  UPLOAD: 'text-sky-700 bg-sky-50',
};

function actionColor(action) {
  const key = Object.keys(ACTION_COLORS).find((k) => action.includes(k));
  return key ? ACTION_COLORS[key] : 'text-neutral-600 bg-neutral-100';
}

function AuditLogTab() {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ action: '', resourceType: '', dateFrom: '', dateTo: '' });
  const [pending, setPending] = useState(filters);

  const { data, isLoading } = useQuery({
    queryKey: ['auditLog', page, filters],
    queryFn: () => getAuditLog({
      page,
      limit: 50,
      ...(filters.action && { action: filters.action }),
      ...(filters.resourceType && { resourceType: filters.resourceType }),
      ...(filters.dateFrom && { dateFrom: filters.dateFrom }),
      ...(filters.dateTo && { dateTo: filters.dateTo }),
    }),
    keepPreviousData: true,
  });

  const applyFilters = useCallback(() => {
    setFilters(pending);
    setPage(1);
  }, [pending]);

  const clearFilters = () => {
    const empty = { action: '', resourceType: '', dateFrom: '', dateTo: '' };
    setPending(empty);
    setFilters(empty);
    setPage(1);
  };

  const entries = data?.entries ?? [];
  const pagination = data?.pagination;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">Audit Log</h2>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">
            HIPAA-required record of all access and changes to protected health information.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">Action</label>
            <input
              type="text"
              className="input text-sm"
              placeholder="e.g. CLIENT_READ"
              value={pending.action}
              onChange={(e) => setPending((p) => ({ ...p, action: e.target.value }))}
              onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">Resource type</label>
            <select
              className="input text-sm"
              value={pending.resourceType}
              onChange={(e) => setPending((p) => ({ ...p, resourceType: e.target.value }))}
            >
              <option value="">All</option>
              {['Client', 'Note', 'Appointment', 'Invoice', 'Document', 'User', 'Practice', 'Payment'].map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">From</label>
            <input
              type="date"
              className="input text-sm"
              value={pending.dateFrom}
              onChange={(e) => setPending((p) => ({ ...p, dateFrom: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">To</label>
            <input
              type="date"
              className="input text-sm"
              value={pending.dateTo}
              onChange={(e) => setPending((p) => ({ ...p, dateTo: e.target.value }))}
            />
          </div>
        </div>
        <div className="flex gap-2 mt-3">
          <button onClick={applyFilters} className="btn btn-primary text-sm">Apply</button>
          <button onClick={clearFilters} className="btn btn-secondary text-sm">Clear</button>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-10"><Spinner /></div>
      ) : entries.length === 0 ? (
        <div className="card text-center py-10 text-sm text-neutral-400 dark:text-neutral-500">No audit log entries found.</div>
      ) : (
        <div className="card overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-neutral-100 dark:border-neutral-700 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
                <th className="px-4 py-3">Timestamp</th>
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Action</th>
                <th className="px-4 py-3">Resource</th>
                <th className="px-4 py-3">IP address</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-50 dark:divide-neutral-700">
              {entries.map((entry) => (
                <tr key={entry.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors">
                  <td className="px-4 py-3 text-neutral-500 dark:text-neutral-400 whitespace-nowrap text-xs">
                    {format(new Date(entry.createdAt), 'MMM d, yyyy HH:mm:ss')}
                  </td>
                  <td className="px-4 py-3 text-neutral-700 dark:text-neutral-300 whitespace-nowrap">
                    {entry.user
                      ? `${entry.user.firstName} ${entry.user.lastName}`
                      : <span className="text-neutral-400 dark:text-neutral-500 italic">System</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${actionColor(entry.action)}`}>
                      {entry.action}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-neutral-600 dark:text-neutral-400">
                    {entry.resourceType}
                    {entry.resourceId && (
                      <span className="text-neutral-400 dark:text-neutral-500 text-xs ml-1 font-mono">
                        {entry.resourceId.slice(0, 8)}…
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-neutral-400 dark:text-neutral-500 text-xs font-mono whitespace-nowrap">
                    {entry.ipAddress || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <div className="flex items-center justify-between text-sm text-neutral-600 dark:text-neutral-400">
          <span className="text-neutral-600 dark:text-neutral-400">{pagination.total} total entries</span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="btn btn-secondary disabled:opacity-40"
            >
              Previous
            </button>
            <span className="text-neutral-600 dark:text-neutral-400">Page {page} of {pagination.pages}</span>
            <button
              onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
              disabled={page === pagination.pages}
              className="btn btn-secondary disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function BackupCodesDisplay({ codes, onDone }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(codes.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="space-y-4">
      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4">
        <p className="text-sm font-semibold text-amber-800 dark:text-amber-300 mb-1">Save your backup codes</p>
        <p className="text-xs text-amber-700 dark:text-amber-400">
          Store these codes somewhere safe. Each code can only be used once. If you lose access to your authenticator app, use a backup code to sign in.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-2 font-mono text-sm bg-neutral-100 dark:bg-neutral-800 rounded-lg p-4">
        {codes.map((code) => (
          <span key={code} className="text-neutral-800 dark:text-neutral-200 tracking-widest">{code}</span>
        ))}
      </div>
      <div className="flex gap-3">
        <button onClick={handleCopy} className="btn btn-secondary text-sm">
          {copied ? 'Copied!' : 'Copy all'}
        </button>
        <button onClick={onDone} className="btn btn-primary text-sm">Done</button>
      </div>
    </div>
  );
}

function TwoFactorTab() {
  const queryClient = useQueryClient();
  const [step, setStep] = useState('idle'); // idle | setup | verify | showCodes
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [newBackupCodes, setNewBackupCodes] = useState(null);
  const [disablePassword, setDisablePassword] = useState('');
  const [regenPassword, setRegenPassword] = useState('');

  const { data: profileData } = useQuery({
    queryKey: ['settings', 'profile'],
    queryFn: getProfile,
  });
  const totpEnabled = profileData?.user?.totpEnabled ?? false;

  const setupMutation = useMutation({
    mutationFn: totpSetup,
    onSuccess: (data) => {
      setSetupData(data);
      setStep('verify');
    },
    onError: () => toast.error('Failed to generate setup code'),
  });

  const enableMutation = useMutation({
    mutationFn: () => totpEnable({ secret: setupData.secret, code: verifyCode }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'profile'] });
      setNewBackupCodes(data.data.backupCodes);
      setStep('showCodes');
      setSetupData(null);
      setVerifyCode('');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Invalid code'),
  });

  const disableMutation = useMutation({
    mutationFn: () => totpDisable(disablePassword),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'profile'] });
      setDisablePassword('');
      toast.success('Two-factor authentication disabled');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to disable 2FA'),
  });

  const regenMutation = useMutation({
    mutationFn: () => regenerateBackupCodes(regenPassword),
    onSuccess: (data) => {
      setNewBackupCodes(data.data.backupCodes);
      setRegenPassword('');
      setStep('showCodes');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to regenerate backup codes'),
  });

  if (step === 'showCodes' && newBackupCodes) {
    return (
      <div className="space-y-6 max-w-lg">
        <div className="card">
          <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100 mb-4">Backup Codes</h2>
          <BackupCodesDisplay
            codes={newBackupCodes}
            onDone={() => { setNewBackupCodes(null); setStep('idle'); }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-lg">
      <div className="card">
        <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100 mb-1">Two-Factor Authentication</h2>
        <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-5">
          Add an extra layer of security using a time-based one-time password (TOTP) app such as Google Authenticator or Authy.
        </p>

        {totpEnabled ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-4 py-3 rounded-lg">
              <span className="font-semibold">2FA is enabled</span>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Current password (required to disable)
              </label>
              <input
                type="password"
                className="input"
                value={disablePassword}
                onChange={(e) => setDisablePassword(e.target.value)}
                placeholder="••••••••"
              />
            </div>
            <button
              onClick={() => disableMutation.mutate()}
              disabled={!disablePassword || disableMutation.isPending}
              className="btn btn-danger disabled:opacity-60"
            >
              {disableMutation.isPending ? 'Disabling…' : 'Disable 2FA'}
            </button>
          </div>
        ) : step === 'idle' ? (
          <button
            onClick={() => setupMutation.mutate()}
            disabled={setupMutation.isPending}
            className="btn btn-primary disabled:opacity-60"
          >
            {setupMutation.isPending ? 'Generating…' : 'Set up 2FA'}
          </button>
        ) : step === 'verify' && setupData ? (
          <div className="space-y-4">
            <p className="text-sm text-neutral-600 dark:text-neutral-400">
              Scan this QR code with your authenticator app, then enter the 6-digit code to confirm.
            </p>
            <div className="flex justify-center">
              <img src={setupData.qrDataUrl} alt="TOTP QR code" className="w-48 h-48" />
            </div>
            <p className="text-xs text-neutral-400 dark:text-neutral-500 break-all text-center">
              Manual key: <span className="font-mono">{setupData.secret}</span>
            </p>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Verification code
              </label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                className="input text-center tracking-widest text-lg"
                placeholder="000000"
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => enableMutation.mutate()}
                disabled={verifyCode.length !== 6 || enableMutation.isPending}
                className="btn btn-primary disabled:opacity-60"
              >
                {enableMutation.isPending ? 'Verifying…' : 'Enable 2FA'}
              </button>
              <button
                onClick={() => { setStep('idle'); setSetupData(null); setVerifyCode(''); }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {totpEnabled && (
        <div className="card">
          <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100 mb-1">Backup Codes</h2>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4">
            Lost your backup codes? Generate a new set. This will invalidate any previously issued codes.
          </p>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Current password to confirm
              </label>
              <input
                type="password"
                className="input"
                value={regenPassword}
                onChange={(e) => setRegenPassword(e.target.value)}
                placeholder="••••••••"
              />
            </div>
            <button
              onClick={() => regenMutation.mutate()}
              disabled={!regenPassword || regenMutation.isPending}
              className="btn btn-secondary disabled:opacity-60"
            >
              {regenMutation.isPending ? 'Generating…' : 'Regenerate backup codes'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function StripeConnectTab() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [disconnectConfirm, setDisconnectConfirm] = useState(false);

  const { data: status, isLoading } = useQuery({
    queryKey: ['stripeConnect', 'status'],
    queryFn: getStripeConnectStatus,
  });

  // Handle return from Stripe onboarding
  useEffect(() => {
    if (searchParams.get('stripe_return') || searchParams.get('stripe_refresh')) {
      queryClient.invalidateQueries({ queryKey: ['stripeConnect', 'status'] });
      setSearchParams({}, { replace: true });
      if (searchParams.get('stripe_return')) toast.success('Stripe setup complete — checking status…');
    }
  }, [searchParams, setSearchParams, queryClient]);

  const onboardMutation = useMutation({
    mutationFn: initiateStripeOnboarding,
    onSuccess: ({ url }) => { window.location.href = url; },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to start Stripe onboarding'),
  });

  const disconnectMutation = useMutation({
    mutationFn: disconnectStripe,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stripeConnect', 'status'] });
      setDisconnectConfirm(false);
      toast.success('Stripe account disconnected');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to disconnect'),
  });

  const dashboardMutation = useMutation({
    mutationFn: getStripeDashboardLink,
    onSuccess: ({ url }) => window.open(url, '_blank', 'noopener,noreferrer'),
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to open dashboard'),
  });

  if (isLoading) return <div className="flex justify-center py-10"><Spinner /></div>;

  const isConnected = status?.connected;
  const isReady = isConnected && status?.chargesEnabled;

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="card">
        <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100 mb-1">Stripe Payments</h2>
        <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-6">
          Connect your own Stripe account so client payments go directly to you.
          If no account is connected, payments use the platform&apos;s Stripe key instead.
        </p>

        {!isConnected && (
          <button
            onClick={() => onboardMutation.mutate()}
            disabled={onboardMutation.isPending}
            className="btn btn-primary disabled:opacity-60"
          >
            {onboardMutation.isPending ? 'Redirecting…' : 'Connect with Stripe'}
          </button>
        )}

        {isConnected && !isReady && (
          <div className="space-y-4">
            <div className="flex items-start gap-3 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 p-4 text-sm text-yellow-800 dark:text-yellow-300">
              <span className="font-medium">Stripe setup incomplete.</span>
              <span>Finish setting up your account to accept payments.</span>
            </div>
            {status?.requirements?.length > 0 && (
              <p className="text-sm text-neutral-600 dark:text-neutral-400">
                Still needed: {status.requirements.join(', ')}
              </p>
            )}
            <button
              onClick={() => onboardMutation.mutate()}
              disabled={onboardMutation.isPending}
              className="btn btn-primary disabled:opacity-60"
            >
              {onboardMutation.isPending ? 'Redirecting…' : 'Continue Stripe setup'}
            </button>
          </div>
        )}

        {isReady && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-green-100 dark:bg-green-900/30 px-3 py-1 text-sm font-medium text-green-700 dark:text-green-400">
                Connected
              </span>
              <span className="text-sm text-neutral-500 dark:text-neutral-400 font-mono">
                {status.accountId?.slice(-12)}
              </span>
            </div>
            <div className="text-sm text-neutral-600 dark:text-neutral-400 space-y-1">
              <p>Charges enabled: <strong className="text-neutral-900 dark:text-neutral-100">Yes</strong></p>
              <p>Payouts enabled: <strong className="text-neutral-900 dark:text-neutral-100">{status.payoutsEnabled ? 'Yes' : 'Pending'}</strong></p>
            </div>

            <button
              onClick={() => dashboardMutation.mutate()}
              disabled={dashboardMutation.isPending}
              className="btn btn-primary disabled:opacity-60"
            >
              {dashboardMutation.isPending ? 'Opening…' : 'View Stripe Dashboard'}
            </button>

            {!disconnectConfirm ? (
              <button
                onClick={() => setDisconnectConfirm(true)}
                className="btn btn-secondary text-danger-600 hover:text-danger-700"
              >
                Disconnect Stripe account
              </button>
            ) : (
              <div className="rounded-lg border border-danger-200 dark:border-danger-700 bg-danger-50 dark:bg-danger-900/20 p-4 space-y-3">
                <p className="text-sm text-danger-800 dark:text-danger-300">
                  Clients will no longer be able to pay online until you reconnect. Are you sure?
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => disconnectMutation.mutate()}
                    disabled={disconnectMutation.isPending}
                    className="btn btn-danger disabled:opacity-60"
                  >
                    {disconnectMutation.isPending ? 'Disconnecting…' : 'Yes, disconnect'}
                  </button>
                  <button onClick={() => setDisconnectConfirm(false)} className="btn btn-secondary">
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const REMINDER_WINDOW_OPTIONS = [
  { hours: 2,  label: '2 hours before' },
  { hours: 4,  label: '4 hours before' },
  { hours: 12, label: '12 hours before' },
  { hours: 24, label: '24 hours before (1 day)' },
  { hours: 48, label: '48 hours before (2 days)' },
  { hours: 72, label: '72 hours before (3 days)' },
];

function RemindersTab() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['settings', 'reminders'],
    queryFn: getReminderSettings,
  });

  const [selected, setSelected] = useState(null);

  // Initialise once data loads
  const reminderWindows = data?.reminders?.reminderWindows ?? [24];
  if (selected === null && reminderWindows) {
    setSelected(reminderWindows);
  }

  const mutation = useMutation({
    mutationFn: () => updateReminderSettings({ reminderWindows: selected }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'reminders'] });
      toast.success('Reminder settings saved');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to save'),
  });

  const toggle = (hours) => {
    setSelected((prev) =>
      prev.includes(hours) ? prev.filter((h) => h !== hours) : [...prev, hours]
    );
  };

  if (isLoading) return <div className="flex justify-center py-10"><Spinner /></div>;

  return (
    <div className="max-w-lg space-y-6">
      <div className="card space-y-4">
        <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">
          Appointment Reminders
        </h2>
        <p className="text-sm text-neutral-500 dark:text-neutral-400">
          Choose when to send reminders to clients before their appointments.
          Reminders are only sent once per window per appointment.
        </p>
        <div className="flex flex-wrap gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
            Email — always on
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
            SMS — opt-in per client (requires Twilio)
          </span>
        </div>
        <div className="space-y-2">
          {REMINDER_WINDOW_OPTIONS.map(({ hours, label }) => (
            <label key={hours} className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={(selected ?? reminderWindows).includes(hours)}
                onChange={() => toggle(hours)}
                className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-neutral-700 dark:text-neutral-300">{label}</span>
            </label>
          ))}
        </div>
        {(selected ?? reminderWindows).length === 0 && (
          <p className="text-xs text-amber-600 dark:text-amber-400">
            No windows selected — reminders will not be sent.
          </p>
        )}
      </div>
      <div className="flex justify-end">
        <button
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
          className="btn btn-primary disabled:opacity-60"
        >
          {mutation.isPending ? 'Saving…' : 'Save reminder settings'}
        </button>
      </div>
    </div>
  );
}

// ── Fee Schedule Tab ──────────────────────────────────────────────────────────

const feeItemSchema = z.object({
  cptCode: z.string().min(1, 'Required').max(10, 'Max 10 chars'),
  description: z.string().max(255).optional().or(z.literal('')),
  standardRate: z.coerce.number().min(0.01, 'Required'),
  isCovered: z.boolean().default(true),
});

const payerRateSchema = z.object({
  payerName: z.string().min(1, 'Required').max(255),
  payerId: z.string().max(50).optional().or(z.literal('')),
  cptCode: z.string().min(1, 'Required').max(10),
  contractedRate: z.coerce.number().min(0, 'Required'),
  effectiveDate: z.string().min(1, 'Required'),
  endDate: z.string().optional().or(z.literal('')),
});

function FeeScheduleTab() {
  const queryClient = useQueryClient();
  const [itemEditing, setItemEditing] = useState(null);
  const [rateEditing, setRateEditing] = useState(null);

  const { data: itemsData = {}, isLoading: itemsLoading } = useQuery({
    queryKey: ['feeSchedule'],
    queryFn: getFeeScheduleItems,
  });
  const items = itemsData.items ?? [];

  const { data: ratesData = {}, isLoading: ratesLoading } = useQuery({
    queryKey: ['payerRates'],
    queryFn: () => getPayerRates(),
  });
  const rates = ratesData.rates ?? [];

  // ── Fee item form ─────────────────────────────────────────────────────────
  const {
    register: regItem,
    handleSubmit: handleItemSubmit,
    reset: resetItem,
    formState: { errors: itemErrors },
  } = useForm({ resolver: zodResolver(feeItemSchema) });

  const openNewItem = () => {
    resetItem({ cptCode: '', description: '', standardRate: '', isCovered: true });
    setItemEditing('new');
  };
  const openEditItem = (item) => {
    resetItem({
      cptCode: item.cptCode,
      description: item.description ?? '',
      standardRate: item.standardRate,
      isCovered: item.isCovered,
    });
    setItemEditing(item.id);
  };

  const createItemMut = useMutation({
    mutationFn: createFeeScheduleItem,
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['feeSchedule'] }); toast.success('Rate added'); setItemEditing(null); },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to add rate'),
  });
  const updateItemMut = useMutation({
    mutationFn: updateFeeScheduleItem,
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['feeSchedule'] }); toast.success('Rate updated'); setItemEditing(null); },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to update rate'),
  });
  const deactivateItemMut = useMutation({
    mutationFn: deactivateFeeScheduleItem,
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['feeSchedule'] }); toast.success('Rate deactivated'); },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed'),
  });

  const onItemSubmit = (data) => {
    const payload = { ...data, cptCode: data.cptCode.toUpperCase() };
    if (itemEditing === 'new') createItemMut.mutate(payload);
    else updateItemMut.mutate({ id: itemEditing, ...payload });
  };

  // ── Payer rate form ───────────────────────────────────────────────────────
  const {
    register: regRate,
    handleSubmit: handleRateSubmit,
    reset: resetRate,
    formState: { errors: rateErrors },
  } = useForm({ resolver: zodResolver(payerRateSchema) });

  const openNewRate = () => {
    resetRate({ payerName: '', payerId: '', cptCode: '', contractedRate: '', effectiveDate: new Date().toISOString().slice(0, 10), endDate: '' });
    setRateEditing('new');
  };
  const openEditRate = (rate) => {
    resetRate({
      payerName: rate.payerName,
      payerId: rate.payerId ?? '',
      cptCode: rate.cptCode,
      contractedRate: rate.contractedRate,
      effectiveDate: rate.effectiveDate,
      endDate: rate.endDate ?? '',
    });
    setRateEditing(rate.id);
  };

  const createRateMut = useMutation({
    mutationFn: createPayerRate,
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['payerRates'] }); toast.success('Rate added'); setRateEditing(null); },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to add rate'),
  });
  const updateRateMut = useMutation({
    mutationFn: updatePayerRate,
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['payerRates'] }); toast.success('Rate updated'); setRateEditing(null); },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to update rate'),
  });
  const deleteRateMut = useMutation({
    mutationFn: deletePayerRate,
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['payerRates'] }); toast.success('Rate deleted'); },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed'),
  });

  const onRateSubmit = (data) => {
    const payload = { ...data, cptCode: data.cptCode.toUpperCase(), endDate: data.endDate || null };
    if (rateEditing === 'new') createRateMut.mutate(payload);
    else updateRateMut.mutate({ id: rateEditing, ...payload });
  };

  const itemSubmitting = createItemMut.isPending || updateItemMut.isPending;
  const rateSubmitting = createRateMut.isPending || updateRateMut.isPending;

  return (
    <div className="max-w-4xl space-y-10">

      {/* ── CPT Rate Card ─────────────────────────────────────────────────── */}
      <div className="space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">CPT Rate Card</h2>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">
              Standard rates for CPT codes and non-covered charges (documentation, late cancellation, etc.).
              Used to auto-fill claim service lines.
            </p>
          </div>
          {itemEditing === null && (
            <button onClick={openNewItem} className="btn btn-primary text-sm shrink-0 ml-4">+ Add rate</button>
          )}
        </div>

        {itemEditing !== null && (
          <div className="card space-y-4">
            <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
              {itemEditing === 'new' ? 'New rate' : 'Edit rate'}
            </h3>
            <form onSubmit={handleItemSubmit(onItemSubmit)} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">CPT / Code *</label>
                  <input type="text" className="input font-mono uppercase" maxLength={10} placeholder="e.g. 90837" {...regItem('cptCode')} />
                  {itemErrors.cptCode && <p className="mt-1 text-xs text-danger-600">{itemErrors.cptCode.message}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Standard rate ($) *</label>
                  <input type="number" step="0.01" min="0.01" className="input" {...regItem('standardRate')} />
                  {itemErrors.standardRate && <p className="mt-1 text-xs text-danger-600">{itemErrors.standardRate.message}</p>}
                </div>
                <div className="flex items-end pb-0.5">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" className="h-4 w-4 rounded border-neutral-300 text-primary-600" {...regItem('isCovered')} />
                    <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">Insurance-covered</span>
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Description</label>
                <input type="text" className="input" placeholder="e.g. Psychotherapy 60 min" {...regItem('description')} />
              </div>
              <div className="flex gap-3 justify-end">
                <button type="button" onClick={() => setItemEditing(null)} className="btn btn-secondary text-sm">Cancel</button>
                <button type="submit" disabled={itemSubmitting} className="btn btn-primary text-sm disabled:opacity-60">
                  {itemSubmitting ? 'Saving…' : itemEditing === 'new' ? 'Add rate' : 'Save changes'}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="card p-0 overflow-x-auto">
          {itemsLoading ? (
            <div className="flex justify-center py-8"><Spinner /></div>
          ) : items.length === 0 ? (
            <p className="text-sm text-neutral-500 dark:text-neutral-400 text-center py-8">No rates configured yet.</p>
          ) : (
            <table className="w-full text-sm min-w-[600px]">
              <thead className="bg-neutral-50 dark:bg-neutral-900/50 border-b border-neutral-200 dark:border-neutral-700">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-neutral-600 dark:text-neutral-400">Code</th>
                  <th className="text-left px-4 py-3 font-medium text-neutral-600 dark:text-neutral-400">Description</th>
                  <th className="text-right px-4 py-3 font-medium text-neutral-600 dark:text-neutral-400">Rate</th>
                  <th className="text-left px-4 py-3 font-medium text-neutral-600 dark:text-neutral-400">Type</th>
                  <th className="text-left px-4 py-3 font-medium text-neutral-600 dark:text-neutral-400">Status</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100 dark:divide-neutral-700">
                {items.map((item) => (
                  <tr key={item.id} className={item.isActive ? '' : 'opacity-50'}>
                    <td className="px-4 py-3">
                      <span className="font-mono font-medium text-neutral-900 dark:text-neutral-100">{item.cptCode}</span>
                    </td>
                    <td className="px-4 py-3 text-neutral-600 dark:text-neutral-400 max-w-xs truncate">
                      {item.description || '—'}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-neutral-900 dark:text-neutral-100">
                      ${parseFloat(item.standardRate).toFixed(2)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        item.isCovered
                          ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                          : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                      }`}>
                        {item.isCovered ? 'Insurance' : 'Self-pay only'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        item.isActive
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                          : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-500'
                      }`}>
                        {item.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2 justify-end">
                        <button onClick={() => openEditItem(item)} className="text-xs text-primary-600 hover:text-primary-800 font-medium">
                          Edit
                        </button>
                        {item.isActive && (
                          <button
                            onClick={() => deactivateItemMut.mutate(item.id)}
                            className="text-xs text-danger-600 hover:text-danger-800 font-medium"
                          >
                            Deactivate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Payer Contracted Rates ────────────────────────────────────────── */}
      <div className="space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">Payer Contracted Rates</h2>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">
              Insurance-specific contracted rates per CPT code and effective date.
              Used to calculate expected reimbursement and flag payment variances.
            </p>
          </div>
          {rateEditing === null && (
            <button onClick={openNewRate} className="btn btn-primary text-sm shrink-0 ml-4">+ Add rate</button>
          )}
        </div>

        {rateEditing !== null && (
          <div className="card space-y-4">
            <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
              {rateEditing === 'new' ? 'New contracted rate' : 'Edit contracted rate'}
            </h3>
            <form onSubmit={handleRateSubmit(onRateSubmit)} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Payer name *</label>
                  <input type="text" className="input" placeholder="e.g. Blue Cross Blue Shield" {...regRate('payerName')} />
                  {rateErrors.payerName && <p className="mt-1 text-xs text-danger-600">{rateErrors.payerName.message}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Payer ID</label>
                  <input type="text" className="input" placeholder="Clearinghouse payer ID" {...regRate('payerId')} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">CPT Code *</label>
                  <input type="text" className="input font-mono uppercase" maxLength={10} {...regRate('cptCode')} />
                  {rateErrors.cptCode && <p className="mt-1 text-xs text-danger-600">{rateErrors.cptCode.message}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Contracted rate ($) *</label>
                  <input type="number" step="0.01" min="0" className="input" {...regRate('contractedRate')} />
                  {rateErrors.contractedRate && <p className="mt-1 text-xs text-danger-600">{rateErrors.contractedRate.message}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Effective date *</label>
                  <input type="date" className="input" {...regRate('effectiveDate')} />
                  {rateErrors.effectiveDate && <p className="mt-1 text-xs text-danger-600">{rateErrors.effectiveDate.message}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    End date <span className="font-normal text-neutral-400">(leave blank if current)</span>
                  </label>
                  <input type="date" className="input" {...regRate('endDate')} />
                </div>
              </div>
              <div className="flex gap-3 justify-end">
                <button type="button" onClick={() => setRateEditing(null)} className="btn btn-secondary text-sm">Cancel</button>
                <button type="submit" disabled={rateSubmitting} className="btn btn-primary text-sm disabled:opacity-60">
                  {rateSubmitting ? 'Saving…' : rateEditing === 'new' ? 'Add rate' : 'Save changes'}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="card p-0 overflow-x-auto">
          {ratesLoading ? (
            <div className="flex justify-center py-8"><Spinner /></div>
          ) : rates.length === 0 ? (
            <p className="text-sm text-neutral-500 dark:text-neutral-400 text-center py-8">
              No contracted rates configured. Add rates to enable payment reconciliation.
            </p>
          ) : (
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-neutral-50 dark:bg-neutral-900/50 border-b border-neutral-200 dark:border-neutral-700">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-neutral-600 dark:text-neutral-400">Payer</th>
                  <th className="text-left px-4 py-3 font-medium text-neutral-600 dark:text-neutral-400">CPT</th>
                  <th className="text-right px-4 py-3 font-medium text-neutral-600 dark:text-neutral-400">Contracted Rate</th>
                  <th className="text-left px-4 py-3 font-medium text-neutral-600 dark:text-neutral-400">Effective</th>
                  <th className="text-left px-4 py-3 font-medium text-neutral-600 dark:text-neutral-400">End Date</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100 dark:divide-neutral-700">
                {rates.map((rate) => (
                  <tr key={rate.id}>
                    <td className="px-4 py-3 text-neutral-900 dark:text-neutral-100">
                      {rate.payerName}
                      {rate.payerId && <span className="text-xs text-neutral-400 ml-1">({rate.payerId})</span>}
                    </td>
                    <td className="px-4 py-3 font-mono font-medium text-neutral-900 dark:text-neutral-100">{rate.cptCode}</td>
                    <td className="px-4 py-3 text-right font-medium text-neutral-900 dark:text-neutral-100">
                      ${parseFloat(rate.contractedRate).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-neutral-600 dark:text-neutral-400">{rate.effectiveDate}</td>
                    <td className="px-4 py-3 text-neutral-600 dark:text-neutral-400">
                      {rate.endDate || <span className="text-green-600 dark:text-green-400 text-xs font-medium">Current</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2 justify-end">
                        <button onClick={() => openEditRate(rate)} className="text-xs text-primary-600 hover:text-primary-800 font-medium">Edit</button>
                        <button onClick={() => deleteRateMut.mutate(rate.id)} className="text-xs text-danger-600 hover:text-danger-800 font-medium">Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Integrations Tab ─────────────────────────────────────────────────────────

function ZoomIntegrationTab() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [disconnectConfirm, setDisconnectConfirm] = useState(false);
  const oauthHandled = useRef(false);

  const { data: status, isLoading } = useQuery({
    queryKey: ['zoom', 'status'],
    queryFn: getZoomStatus,
  });

  // Handle return from Zoom OAuth — Zoom appends ?code=&state= to the redirect URI
  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const zoomReturn = searchParams.get('zoom_return');
    if (!zoomReturn || !code || !state) return;
    if (oauthHandled.current) return;
    oauthHandled.current = true;

    setSearchParams({ tab: 'integrations' }, { replace: true });
    completeZoomOAuth(code, state)
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ['zoom', 'status'] });
        toast.success('Zoom account connected');
      })
      .catch((err) => toast.error(err.response?.data?.message || 'Zoom connection failed'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const connectMutation = useMutation({
    mutationFn: getZoomAuthUrl,
    onSuccess: ({ url }) => { window.location.href = url; },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to start Zoom authorization'),
  });

  const disconnectMutation = useMutation({
    mutationFn: disconnectZoom,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['zoom', 'status'] });
      setDisconnectConfirm(false);
      toast.success('Zoom account disconnected');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to disconnect Zoom'),
  });

  if (isLoading) return <div className="flex justify-center py-10"><Spinner /></div>;

  const isConnected = status?.connected;

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="card">
        <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100 mb-1">Zoom Video</h2>
        <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-6">
          Connect your practice&apos;s Zoom account to automatically create Zoom meetings for telehealth
          appointments. Requires a Zoom paid plan with a signed BAA for HIPAA compliance.
        </p>

        {!isConnected ? (
          <button
            onClick={() => connectMutation.mutate()}
            disabled={connectMutation.isPending}
            className="btn btn-primary disabled:opacity-60"
          >
            {connectMutation.isPending ? 'Redirecting to Zoom…' : 'Connect with Zoom'}
          </button>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-green-100 dark:bg-green-900/30 px-3 py-1 text-sm font-medium text-green-700 dark:text-green-400">
                Connected
              </span>
              {status?.zoomUserId && (
                <span className="text-xs text-neutral-500 dark:text-neutral-400 font-mono">{status.zoomUserId}</span>
              )}
            </div>

            {!disconnectConfirm ? (
              <button
                onClick={() => setDisconnectConfirm(true)}
                className="btn btn-secondary text-sm"
              >
                Disconnect Zoom account
              </button>
            ) : (
              <div className="rounded-lg border border-danger-200 dark:border-danger-700 bg-danger-50 dark:bg-danger-900/20 p-4 space-y-3">
                <p className="text-sm text-danger-800 dark:text-danger-300">
                  Telehealth appointments will no longer have Zoom meetings created automatically. Are you sure?
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => disconnectMutation.mutate()}
                    disabled={disconnectMutation.isPending}
                    className="btn btn-danger disabled:opacity-60"
                  >
                    {disconnectMutation.isPending ? 'Disconnecting…' : 'Yes, disconnect'}
                  </button>
                  <button onClick={() => setDisconnectConfirm(false)} className="btn btn-secondary">Cancel</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Billing Tab ──────────────────────────────────────────────────────────────

const STATUS_LABELS = {
  trialing: { label: 'Free trial', variant: 'info' },
  active: { label: 'Active', variant: 'success' },
  past_due: { label: 'Past due', variant: 'warning' },
  canceled: { label: 'Canceled', variant: 'default' },
  unpaid: { label: 'Unpaid', variant: 'error' },
};

function BillingTab() {

  const { data: sub, isLoading } = useQuery({
    queryKey: ['subscriptionStatus'],
    queryFn: getSubscriptionStatus,
    staleTime: 60 * 1000,
  });

  const checkoutMutation = useMutation({
    mutationFn: () => createCheckoutSession('solo'),
    onSuccess: ({ url }) => { window.location.href = url; },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to start checkout'),
  });

  const portalMutation = useMutation({
    mutationFn: createPortalSession,
    onSuccess: ({ url }) => { window.location.href = url; },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to open billing portal'),
  });

  if (isLoading) return <div className="flex justify-center py-20"><Spinner /></div>;
  if (!sub) return null;

  const { status, plan, trialEndsAt, currentPeriodEnd, isSubscribed, hasPaymentMethod, freeAccount } = sub;
  const statusInfo = freeAccount
    ? { label: 'Complimentary', variant: 'success' }
    : (STATUS_LABELS[status] ?? STATUS_LABELS.canceled);

  const fmtDate = (d) => d ? format(parseISO(d), 'MMMM d, yyyy') : null;

  return (
    <div className="max-w-2xl space-y-6">
      {/* Current plan card */}
      <div className="card">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100">
              {freeAccount ? 'Complimentary Access' : plan === 'solo' ? 'Solo Plan' : 'Subscription'}
            </h2>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">
              {freeAccount ? 'No charge — provided by Sage' : plan === 'solo' ? '$49 / month' : '—'}
            </p>
          </div>
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
            ${freeAccount ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
              status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
              status === 'trialing' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' :
              status === 'past_due' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' :
              'bg-neutral-100 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-300'}`}>
            {statusInfo.label}
          </span>
        </div>

        {!freeAccount && (
          <div className="grid grid-cols-2 gap-4 text-sm mb-5">
            {status === 'trialing' && trialEndsAt && (
              <div>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-0.5">Trial ends</p>
                <p className="font-medium text-neutral-900 dark:text-neutral-100">{fmtDate(trialEndsAt)}</p>
              </div>
            )}
            {currentPeriodEnd && status === 'active' && (
              <div>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-0.5">Next billing date</p>
                <p className="font-medium text-neutral-900 dark:text-neutral-100">{fmtDate(currentPeriodEnd)}</p>
              </div>
            )}
          </div>
        )}

        {freeAccount ? null : !isSubscribed ? (
          <div className="space-y-3">
            <p className="text-sm text-neutral-600 dark:text-neutral-400">
              {status === 'trialing'
                ? 'Subscribe now to keep access after your trial ends.'
                : 'Resubscribe to restore access to your practice.'}
            </p>
            <button
              onClick={() => checkoutMutation.mutate()}
              disabled={checkoutMutation.isPending}
              className="btn btn-primary disabled:opacity-60"
            >
              {checkoutMutation.isPending ? 'Redirecting…' : 'Subscribe — $49/month'}
            </button>
          </div>
        ) : hasPaymentMethod ? (
          <button
            onClick={() => portalMutation.mutate()}
            disabled={portalMutation.isPending}
            className="btn btn-secondary disabled:opacity-60"
          >
            {portalMutation.isPending ? 'Opening…' : 'Manage subscription'}
          </button>
        ) : (
          <button
            onClick={() => checkoutMutation.mutate()}
            disabled={checkoutMutation.isPending}
            className="btn btn-primary disabled:opacity-60"
          >
            {checkoutMutation.isPending ? 'Redirecting…' : 'Add payment method'}
          </button>
        )}
      </div>

      {/* Plan features */}
      <div className="card">
        <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 mb-3">What&apos;s included</h3>
        <ul className="space-y-2 text-sm text-neutral-600 dark:text-neutral-400">
          {[
            '1 practitioner',
            'Unlimited clients',
            'Scheduling, telehealth & recurring appointments',
            'Clinical notes (SOAP/DAP) & intake forms',
            'Insurance billing, ERA parsing & eligibility',
            'Client portal with payments & secure messaging',
            'FHIR R4 export',
            'HIPAA-compliant (BAA available on request)',
          ].map((f) => (
            <li key={f} className="flex items-center gap-2">
              <svg className="h-4 w-4 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {f}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

const PRESET_COLORS = ['#8b5cf6','#6366f1','#f59e0b','#10b981','#ef4444','#06b6d4','#f97316','#ec4899'];

function CalendarsTab() {
  const queryClient = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [newUrl, setNewUrl] = useState('');
  const [newColor, setNewColor] = useState('#8b5cf6');

  const { data: subs = [], isLoading } = useQuery({
    queryKey: ['calendarSubscriptions'],
    queryFn: getCalendarSubscriptions,
  });

  const addMutation = useMutation({
    mutationFn: () => createCalendarSubscription({ name: newName.trim(), url: newUrl.trim(), color: newColor }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendarSubscriptions'] });
      queryClient.invalidateQueries({ queryKey: ['externalCalendarEvents'] });
      toast.success('Calendar added');
      setShowAdd(false);
      setNewName('');
      setNewUrl('');
      setNewColor('#8b5cf6');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to add calendar'),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, isEnabled }) => updateCalendarSubscription(id, { isEnabled }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['calendarSubscriptions'] }),
    onError: () => toast.error('Failed to update calendar'),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteCalendarSubscription,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendarSubscriptions'] });
      queryClient.invalidateQueries({ queryKey: ['externalCalendarEvents'] });
      toast.success('Calendar removed');
    },
    onError: () => toast.error('Failed to remove calendar'),
  });

  const refreshMutation = useMutation({
    mutationFn: refreshCalendarSubscription,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['externalCalendarEvents'] });
      toast.success('Calendar will refresh on next load');
    },
    onError: () => toast.error('Failed to refresh calendar'),
  });

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">External Calendars</h2>
        <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
          Subscribe to external calendars via iCal URL. Events appear as read-only blocks in your schedule view.
        </p>
      </div>

      {/* How-to instructions */}
      <div className="rounded-lg border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/50 p-4 space-y-3">
        <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">How to get your calendar URL</p>
        <div className="space-y-2 text-sm text-neutral-600 dark:text-neutral-400">
          <div>
            <span className="font-medium text-neutral-800 dark:text-neutral-200">Google Calendar: </span>
            Settings → [select calendar] → Integrate calendar → <em>Secret address in iCal format</em>
          </div>
          <div>
            <span className="font-medium text-neutral-800 dark:text-neutral-200">Apple Calendar: </span>
            Right-click calendar → Share Calendar → Copy Link (enable Public Calendar)
          </div>
          <div>
            <span className="font-medium text-neutral-800 dark:text-neutral-200">Outlook / Microsoft 365: </span>
            Calendar settings → Shared calendars → Publish calendar → Copy ICS link
          </div>
        </div>
      </div>

      {/* Subscription list */}
      {isLoading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : subs.length === 0 && !showAdd ? (
        <div className="text-center py-10 text-sm text-neutral-400 dark:text-neutral-500">
          No external calendars connected yet.
        </div>
      ) : (
        <div className="space-y-3">
          {subs.map((sub) => (
            <div key={sub.id} className="flex items-center gap-3 p-4 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800">
              <div
                className="w-4 h-4 rounded-full shrink-0"
                style={{ backgroundColor: sub.color }}
              />
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium truncate ${sub.isEnabled ? 'text-neutral-900 dark:text-neutral-100' : 'text-neutral-400 line-through'}`}>
                  {sub.name}
                </p>
                <p className="text-xs text-neutral-400 dark:text-neutral-500 truncate">{sub.url}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => refreshMutation.mutate(sub.id)}
                  disabled={refreshMutation.isPending}
                  className="text-xs text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300"
                  title="Refresh"
                >
                  ↻
                </button>
                <label className="flex items-center gap-1.5 cursor-pointer text-xs text-neutral-500 dark:text-neutral-400">
                  <input
                    type="checkbox"
                    checked={sub.isEnabled}
                    onChange={(e) => toggleMutation.mutate({ id: sub.id, isEnabled: e.target.checked })}
                    className="rounded"
                  />
                  Active
                </label>
                <button
                  onClick={() => deleteMutation.mutate(sub.id)}
                  className="text-xs text-danger-500 hover:text-danger-700"
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add form */}
      {showAdd ? (
        <div className="rounded-lg border border-neutral-200 dark:border-neutral-700 p-4 space-y-4">
          <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">Add calendar</p>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Name</label>
            <input
              type="text"
              className="input"
              placeholder="e.g. Personal Google Calendar"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">iCal URL</label>
            <input
              type="url"
              className="input font-mono text-xs"
              placeholder="https://calendar.google.com/calendar/ical/..."
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Color</label>
            <div className="flex gap-2">
              {PRESET_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setNewColor(c)}
                  className={`w-6 h-6 rounded-full transition-transform ${newColor === c ? 'scale-125 ring-2 ring-offset-1 ring-neutral-400' : 'hover:scale-110'}`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>
          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={() => { setShowAdd(false); setNewName(''); setNewUrl(''); setNewColor('#8b5cf6'); }}
              className="btn btn-secondary btn-sm"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => addMutation.mutate()}
              disabled={!newName.trim() || !newUrl.trim() || addMutation.isPending}
              className="btn btn-primary btn-sm disabled:opacity-60"
            >
              {addMutation.isPending ? 'Connecting…' : 'Add calendar'}
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setShowAdd(true)}
          className="btn btn-secondary text-sm"
        >
          + Add calendar
        </button>
      )}
    </div>
  );
}

// ── Contact Widget Tab ────────────────────────────────────────────────────────

function ContactWidgetTab() {
  const queryClient = useQueryClient();
  const [greeting, setGreeting] = useState('');
  const [copied, setCopied] = useState(false);
  const [showRegenConfirm, setShowRegenConfirm] = useState(false);

  const { data: practice, isLoading } = useQuery({
    queryKey: ['settings', 'practice'],
    queryFn: getPractice,
  });

  const widgetToken = practice?.practice?.widgetToken;
  const widgetEnabled = practice?.practice?.settings?.widget?.enabled === true;
  const currentGreeting = practice?.practice?.settings?.widget?.greeting || '';
  const baaAccepted = !!practice?.practice?.baaAcceptedAt;

  // Sync greeting field when practice data loads
  useEffect(() => {
    setGreeting(currentGreeting);
  }, [currentGreeting]);

  const enableMutation = useMutation({
    mutationFn: () => enableWidget({ greeting: greeting.trim() || undefined }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'practice'] });
      toast.success(widgetEnabled ? 'Widget settings saved' : 'Contact widget enabled');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to save widget settings'),
  });

  const regenMutation = useMutation({
    mutationFn: regenerateWidgetToken,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'practice'] });
      setShowRegenConfirm(false);
      toast.success('Embed token regenerated — update the script tag on your website');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to regenerate token'),
  });

  const embedCode = widgetToken
    ? `<script src="${window.location.origin}/widget.js"\n        data-practice="${widgetToken}"></script>`
    : null;

  const copyEmbed = () => {
    if (!embedCode) return;
    navigator.clipboard.writeText(embedCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (isLoading) return <div className="flex justify-center py-10"><Spinner /></div>;

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Info card */}
      <div className="card">
        <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-100 mb-1">
          Secure Contact Widget
        </h2>
        <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-6">
          Embed a secure contact form on your website. Prospective clients can send you a message
          and attach documents (e.g. insurance cards) before signing up. All submissions are
          encrypted and HIPAA-compliant.
        </p>

        {!baaAccepted && (
          <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 p-4 text-sm text-amber-800 dark:text-amber-300 mb-4">
            You must accept the Business Associate Agreement before enabling the contact widget.
            Go to <strong>Settings → Practice</strong> to accept the BAA.
          </div>
        )}

        {/* Greeting text */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
            Widget greeting message
          </label>
          <textarea
            className="input"
            rows={2}
            maxLength={500}
            placeholder={`Send a secure message to ${practice?.practice?.name || 'our practice'}`}
            value={greeting}
            onChange={(e) => setGreeting(e.target.value)}
            disabled={!baaAccepted}
          />
          <p className="text-xs text-neutral-400 mt-1">
            Shown at the top of the contact form on your website.
          </p>
        </div>

        <button
          onClick={() => enableMutation.mutate()}
          disabled={!baaAccepted || enableMutation.isPending}
          className="btn btn-primary disabled:opacity-60"
        >
          {enableMutation.isPending
            ? 'Saving…'
            : widgetEnabled
            ? 'Save settings'
            : 'Enable widget'}
        </button>
      </div>

      {/* Embed code — only shown once widget is enabled */}
      {widgetEnabled && widgetToken && (
        <div className="card">
          <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 mb-1">
            Embed code
          </h3>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-3">
            Paste this snippet before the closing <code className="text-xs bg-neutral-100 dark:bg-neutral-800 px-1 py-0.5 rounded">&lt;/body&gt;</code> tag
            on any page of your website.
          </p>

          <div className="relative">
            <pre className="bg-neutral-900 text-green-400 text-xs rounded-lg p-4 overflow-x-auto whitespace-pre-wrap break-all">
              {embedCode}
            </pre>
            <button
              onClick={copyEmbed}
              className="absolute top-2 right-2 text-xs px-2 py-1 rounded bg-neutral-700 hover:bg-neutral-600 text-white transition-colors"
            >
              {copied ? '✓ Copied' : 'Copy'}
            </button>
          </div>

          <div className="mt-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                If you suspect your embed code has been misused, regenerate the token.
                The old embed code on your website will stop working — update it with the new snippet.
              </p>
            </div>
          </div>

          {!showRegenConfirm ? (
            <button
              onClick={() => setShowRegenConfirm(true)}
              className="mt-3 text-sm text-red-600 hover:text-red-700 dark:text-red-400 font-medium"
            >
              Regenerate embed token…
            </button>
          ) : (
            <div className="mt-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 p-4">
              <p className="text-sm text-red-800 dark:text-red-300 mb-3">
                This will invalidate your current embed code. You will need to update the script
                tag on your website with the new token.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => regenMutation.mutate()}
                  disabled={regenMutation.isPending}
                  className="btn btn-danger text-sm disabled:opacity-60"
                >
                  {regenMutation.isPending ? 'Regenerating…' : 'Yes, regenerate'}
                </button>
                <button
                  onClick={() => setShowRegenConfirm(false)}
                  className="btn btn-secondary text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* How it works */}
      <div className="card">
        <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 mb-3">
          How it works
        </h3>
        <ol className="space-y-2 text-sm text-neutral-600 dark:text-neutral-400 list-decimal list-inside">
          <li>A chat bubble appears on your website for visitors to click.</li>
          <li>They enter their email or phone to receive a one-time verification code.</li>
          <li>After verifying, they fill out a secure contact form and can attach documents.</li>
          <li>The submission appears in your <strong>Messages</strong> tab as a <em>Contact Request</em>.</li>
          <li>When you reply, the prospective client is notified by email or SMS.</li>
          <li>You can convert them to a full client any time from their profile.</li>
        </ol>
      </div>
    </div>
  );
}

const TABS = [
  { id: 'profile', label: 'Profile' },
  { id: 'practice', label: 'Practice' },
  { id: 'appointmentTypes', label: 'Appointment Types' },
  { id: 'feeSchedule', label: 'Fee Schedule' },
  { id: 'reminders', label: 'Reminders' },
  { id: 'calendars', label: 'Calendars' },
  { id: 'security', label: '2FA' },
  { id: 'payments', label: 'Payments' },
  { id: 'billing', label: 'Billing' },
  { id: 'integrations', label: 'Integrations' },
  { id: 'widget', label: 'Contact Widget' },
  { id: 'auditLog', label: 'Audit Log' },
];

export default function Settings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const validTabs = TABS.map((t) => t.id);
  const [activeTab, setActiveTab] = useState(validTabs.includes(tabParam) ? tabParam : 'profile');

  const switchTab = (id) => {
    setActiveTab(id);
    setSearchParams(id !== 'profile' ? { tab: id } : {}, { replace: true });
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mb-6">Settings</h1>

      {/* Tab bar */}
      <div className="border-b border-neutral-200 dark:border-neutral-700 mb-6">
        <nav className="flex gap-6">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => switchTab(tab.id)}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'profile' && <ProfileTab />}
      {activeTab === 'practice' && <PracticeTab />}
      {activeTab === 'appointmentTypes' && <AppointmentTypesTab />}
      {activeTab === 'feeSchedule' && <FeeScheduleTab />}
      {activeTab === 'reminders' && <RemindersTab />}
      {activeTab === 'calendars' && <CalendarsTab />}
      {activeTab === 'security' && <TwoFactorTab />}
      {activeTab === 'payments' && <StripeConnectTab />}
      {activeTab === 'billing' && <BillingTab />}
      {activeTab === 'integrations' && <ZoomIntegrationTab />}
      {activeTab === 'widget' && <ContactWidgetTab />}
      {activeTab === 'auditLog' && <AuditLogTab />}
    </div>
  );
}
